// ChartParent.tsx
import React from "react";

// App imports.
import ChartBar from "./ChartBar";
import ChartStackedBar from "./ChartStackedBar";
import ChartStackedBarPercent from "./ChartStackedBarPercent"; 
import ChartMultiBar from "./ChartMultiBar";
import ChartPie from "./ChartPie";
import ChartLine from "./ChartLine";
import ChartMap from "./ChartMap";
import ChartCombo from "./ChartCombo";
import {
  IHighChartsDataseries,
  ReportDataObject,
  formatChartValue,
  getChartType,
  createReportDataObject,
  formatYAxisLabel,
  formatLabelNumber
} from "../utility";

interface ChartParentProps {
  graphTypes: any;
  fiscalYears: any[];
  grantees: any[];
  title: string;
  subTitle: string;
  reportData: any;
  selectedFields: any[];
}

const ChartParent: React.FC<ChartParentProps> = ({
  graphTypes,
  fiscalYears,
  grantees,
  title: initialTitle,
  subTitle,
  reportData,
  selectedFields,
}) => {
  let title = initialTitle;

  // ---------------- Helper Functions ----------------

  // Returns the abbreviation for a grantee by name.
  const getGranteeAbbreviation = (granteeName: string): string => {
    const found = grantees.filter(g => g.granteeName === granteeName);
    return found.length === 1 ? found[0].granteeAbbreviation : "";
  };

  // Ensures at least one grantee is present and updates the title accordingly.
  const initializeGranteesAndTitle = (): void => {
    if (grantees.length === 0) {
      grantees.push({
        granteeAbbreviation: "US",
        granteeId: "0",
        granteeName: "All Grant Recipients Summed",
        granteeSelected: true,
        regionId: "999"
      });
      if (title.indexOf("All Grant Recipients") === -1) {
        title += " - All Grant Recipients Summed";
      }
    } else if (grantees.length === 1 && title.indexOf(grantees[0].granteeName) === -1) {
      title += " - " + grantees[0].granteeName;
    }
  };

  // Builds map chart data.
  const buildMapChartData = (reportDataObjects: ReportDataObject[]) => {
    const xAxisLabels: string[] = [];
    const mapData: any[] = [];
    reportDataObjects.forEach(data => {
      xAxisLabels.push(data.fiscalYear.toString());
      data.granteeData.forEach(granteeData => {
        granteeData.dataElements.forEach(element => {
          let colValue = Math.round(element.value * 10) / 10;
          if (colValue === 0) colValue = 0.01;
          mapData.push({
            value: Number(colValue),
            code: getGranteeAbbreviation(granteeData.granteeName),
            name: granteeData.granteeName,
            displayVal: formatChartValue(element.value, element.field.tableFieldDataType)
          });
        });
      });
    });
    return { xAxisLabels, mapData };
  };

  // Builds pie chart data.
  const buildPieChartData = (reportDataObjects: ReportDataObject[]) => {
    const series: IHighChartsDataseries[] = [{
      name: " ",
      data: [],
      type: graphTypes.pie.highChartsGraphType
    }];
    // Assumes at least one data element exists.
    reportDataObjects[0].granteeData[0].dataElements.forEach(element => {
      series[0].data.push({
        y: Number(element.value),
        name: element.field.tableFieldDescription,
        prefix: element.field.tableFieldPrefix,
        suffix: element.field.tableFieldSuffix,
        displayVal: formatChartValue(element.value, element.field.tableFieldDataType)
      });
    });
    return { series };
  };

  // Builds combo chart data. Expects exactly two selected fields.
  const buildComboChartData = (reportDataObjects: ReportDataObject[]) => {
    const seriesArray: IHighChartsDataseries[] = selectedFields.map(f => ({
      name: f.tableFieldDescription,
      data: [],
      type: "", // determined below
      yAxis: 0,
      borderColor: "#000"
    }));
    const xAxisLabels: string[] = [];
    reportDataObjects.forEach(yearObj => {
      yearObj.granteeData.forEach(granteeData => {
        // Create label based on grantee count.
        if (grantees.length === 1) {
          xAxisLabels.push(`${yearObj.fiscalYear}`);
        } else {
          xAxisLabels.push(`${granteeData.granteeName} ${yearObj.fiscalYear}`);
        }
        granteeData.dataElements.forEach((element, index) => {
          const seriesType = index === 0 ? "column" : "spline";
          if (!seriesArray[index].type) {
            seriesArray[index].type = seriesType;
            seriesArray[index].yAxis = index === 0 ? 0 : 1;
          }
          seriesArray[index].data.push({
            y: Number(element.value),
            prefix: element.field.tableFieldPrefix,
            suffix: element.field.tableFieldSuffix,
            displayVal: formatChartValue(element.value, element.field.tableFieldDataType)
          });
        });
      });
    });
    return { xAxisLabels, series: seriesArray };
  };

  // Builds chart data for a single selected field.
  const buildSingleFieldChartData = (reportDataObjects: ReportDataObject[]) => {
    const seriesArray: IHighChartsDataseries[] = [];
    grantees.forEach(g => {
      seriesArray.push({
        name: `${selectedFields[0].tableFieldDescription} - ${g.granteeName}`,
        data: [],
        type: getChartType(graphTypes).highChartsGraphType,
        yAxis: 0,
        borderColor: "#000"
      });
    });
    const xAxisLabels: string[] = [];
    reportDataObjects.forEach(data => {
      xAxisLabels.push(data.fiscalYear.toString());
      data.granteeData.forEach((granteeData, index) => {
        granteeData.dataElements.forEach(element => {
          seriesArray[index].data.push({
            y: Number(element.value),
            prefix: element.field.tableFieldPrefix,
            suffix: element.field.tableFieldSuffix,
            displayVal: formatChartValue(element.value, element.field.tableFieldDataType)
          });
        });
      });
    });
    return { xAxisLabels, series: seriesArray };
  };

  // Builds chart data for multiple selected fields (non–combo).
  const buildMultiFieldChartData = (reportDataObjects: ReportDataObject[]) => {
    const seriesArray: IHighChartsDataseries[] = selectedFields.map(f => ({
      name: f.tableFieldDescription,
      data: [],
      type: getChartType(graphTypes).highChartsGraphType,
      yAxis: 0,
      borderColor: "#000"
    }));
    const xAxisLabels: string[] = [];
    reportDataObjects.forEach(year => {
      year.granteeData.forEach(granteeData => {
        if (grantees.length === 1) {
          xAxisLabels.push(`${year.fiscalYear}`);
        } else {
          xAxisLabels.push(`${granteeData.granteeName} ${year.fiscalYear}`);
        }
        granteeData.dataElements.forEach((element, index) => {
          seriesArray[index].data.push({
            y: Number(element.value),
            prefix: element.field.tableFieldPrefix,
            suffix: element.field.tableFieldSuffix,
            displayVal: formatChartValue(element.value, element.field.tableFieldDataType)
          });
        });
      });
    });
    return { xAxisLabels, series: seriesArray };
  };

  // ---------------- End Helper Functions ----------------

  initializeGranteesAndTitle();
  const reportDataObjectArray: ReportDataObject[] = createReportDataObject(reportData, selectedFields);

  // Define variables that will be used to build the child chart component.
  let xAxisCategoryLabelsArray: string[] = [];
  let dataSeriesArray: IHighChartsDataseries[] = [];
  let mapData: any[] = [];

  // Define a format function (passed to child charts) that mimics legacy formatting.
  const formatFunction = function () {
    return formatYAxisLabel(selectedFields[0].tableFieldDataType, this.value ?? this.total);
  };

  // Select the appropriate data builder based on the selected graph type.
  if (graphTypes.map.graphTypeSelected) {
    const { xAxisLabels, mapData: mData } = buildMapChartData(reportDataObjectArray);
    xAxisCategoryLabelsArray = xAxisLabels;
    mapData = mData;
  } else if (graphTypes.pie.graphTypeSelected) {
    const { series } = buildPieChartData(reportDataObjectArray);
    dataSeriesArray = series;
  } else if (graphTypes.combo.graphTypeSelected) {
    const { xAxisLabels, series } = buildComboChartData(reportDataObjectArray);
    xAxisCategoryLabelsArray = xAxisLabels;
    dataSeriesArray = series;
  } else if (selectedFields.length === 1) {
    const { xAxisLabels, series } = buildSingleFieldChartData(reportDataObjectArray);
    xAxisCategoryLabelsArray = xAxisLabels;
    dataSeriesArray = series;
  } else {
    const { xAxisLabels, series } = buildMultiFieldChartData(reportDataObjectArray);
    xAxisCategoryLabelsArray = xAxisLabels;
    dataSeriesArray = series;
  }

  // ---------------- Render Child Chart Components ----------------
  return (
    <span>
      {graphTypes.bar.graphTypeSelected && (
        <ChartBar
          title={title}
          subTitle={subTitle}
          dataSeries={dataSeriesArray}
          xAxisCategoryLabels={xAxisCategoryLabelsArray}
          formatFunction={formatFunction}
        />
      )}

      {graphTypes.multiBar.graphTypeSelected && (
        <ChartMultiBar
          title={title}
          subTitle={subTitle}
          dataSeries={dataSeriesArray}
          xAxisCategoryLabels={xAxisCategoryLabelsArray}
          formatFunction={formatFunction}
        />
      )}

      {graphTypes.stackedBar.graphTypeSelected && (
        <ChartStackedBar
          title={title}
          subTitle={subTitle}
          dataSeries={dataSeriesArray}
          xAxisCategoryLabels={xAxisCategoryLabelsArray}
          formatFunction={formatFunction}
        />
      )}

      {graphTypes.stackedBarPercent.graphTypeSelected && (
        <ChartStackedBarPercent
          title={title}
          subTitle={subTitle}
          dataSeries={dataSeriesArray}
          xAxisCategoryLabels={xAxisCategoryLabelsArray}
          formatFunction={formatFunction}
        />
      )}

      {graphTypes.pie.graphTypeSelected && (
        <ChartPie
          title={title}
          subTitle={subTitle}
          dataSeries={dataSeriesArray}
          xAxisCategoryLabels={xAxisCategoryLabelsArray}
          formatFunction={formatFunction}
        />
      )}

      {graphTypes.line.graphTypeSelected && (
        <ChartLine
          title={title}
          subTitle={subTitle}
          dataSeries={dataSeriesArray}
          xAxisCategoryLabels={xAxisCategoryLabelsArray}
          formatFunction={formatFunction}
        />
      )}

      {graphTypes.combo.graphTypeSelected && (
        <ChartCombo
          title={title}
          subTitle={subTitle}
          dataSeries={dataSeriesArray}
          xAxisCategoryLabels={xAxisCategoryLabelsArray}
          formatFunction={formatFunction}
        />
      )}

      {graphTypes.map.graphTypeSelected && (
        <ChartMap
          title={title}
          subTitle={subTitle}
          seriesData={mapData}
        />
      )}
    </span>
  );
};

export default ChartParent;
